import { Controller } from "@hotwired/stimulus"

const BRAND_SVGS = {
  mastercard: '<svg viewBox="0 0 32 20"><circle cx="10" cy="10" r="10" fill="#eb001b"/><circle cx="22" cy="10" r="10" fill="#f79e1b" fill-opacity="0.86"/><path d="M16 3.8a9.9 9.9 0 0 1 3.7 6.2A9.9 9.9 0 0 1 16 16.2 9.9 9.9 0 0 1 12.3 10 9.9 9.9 0 0 1 16 3.8z" fill="#ff5f00"/></svg>'
}

export default class extends Controller {
  static targets = [
    "cardNumberElement", "cardExpiryElement", "cardCvcElement", "cardErrors", "submitButton",
    "cardNameInput", "cardNameDisplay", "cardNumDisplay", "cardExpDisplay", "cardCvvDisplay",
    "card3D", "cardGlare", "brandLogoSlot", "stage"
  ]
  static values = { 
    publishableKey: String,
    amexLogo: String,
    jcbLogo: String,
    visaLogo: String
  }
  
  connect() {
    this.stripe = Stripe(this.publishableKeyValue)
    this.elements = this.stripe.elements()
    this.setupCardElement()
    this.setupAnimations()
  }

  setupAnimations() {
    // 1. 3Dチルト＆ホログラム効果
    if (this.hasStageTarget && this.hasCard3DTarget) {
      this.stageTarget.addEventListener('mousemove', (e) => {
        if (this.card3DTarget.classList.contains('is-flipped')) return;
        const rect = this.card3DTarget.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;
        
        const centerX = rect.width / 2;
        const centerY = rect.height / 2;

        const rotateX = ((y - centerY) / centerY) * -14;
        const rotateY = ((x - centerX) / centerX) * 14;

        this.card3DTarget.style.transform = `rotateX(${rotateX.toFixed(2)}deg) rotateY(${rotateY.toFixed(2)}deg)`;
        
        // 光沢の座標更新
        if (this.hasCardGlareTarget) {
          const glareX = (x / rect.width) * 100;
          const glareY = (y / rect.height) * 100;
          this.cardGlareTarget.style.setProperty('--glare-x', `${glareX}%`);
          this.cardGlareTarget.style.setProperty('--glare-y', `${glareY}%`);
        }
      });

      this.stageTarget.addEventListener('mouseleave', () => {
        if (!this.card3DTarget.classList.contains('is-flipped')) {
          this.card3DTarget.style.transform = 'rotateX(0deg) rotateY(0deg)';
        }
      });
    }
  }

  updateName(event) {
    if (this.hasCardNameDisplayTarget) {
      this.cardNameDisplayTarget.textContent = event.target.value.trim() || 'YOUR NAME';
    }
  }
  
  setupCardElement() {
    // 個別のカード要素を作成 (CSSに合わせたスタイル)
    const style = {
      base: {
        fontSize: '14px',
        color: '#1f2937',
        fontFamily: "'Plus Jakarta Sans', -apple-system, sans-serif",
        '::placeholder': {
          color: '#9ca3af',
        },
      },
      invalid: {
        color: '#ef4444',
      },
    }
    
    // カード番号
    this.cardNumberElement = this.elements.create('cardNumber', { style })
    this.cardNumberElement.mount(this.cardNumberElementTarget)
    
    // 有効期限
    this.cardExpiryElement = this.elements.create('cardExpiry', { style })
    this.cardExpiryElement.mount(this.cardExpiryElementTarget)
    
    // セキュリティコード
    this.cardCvcElement = this.elements.create('cardCvc', { style })
    this.cardCvcElement.mount(this.cardCvcElementTarget)
    
    // エラー表示とブランド判定
    this.cardNumberElement.on('change', (event) => {
      this.handleError(event)
      if (event.brand) {
        this.updateBrand(event.brand)
      }
      if (this.hasCardNumDisplayTarget) {
        if (!event.empty && event.complete) {
          this.cardNumDisplayTarget.textContent = '•••• •••• •••• ' + (event.brand === 'amex' ? '••••' : '••••'); // We don't get last4 on change usually, but we can just show hidden state
          this.cardNumDisplayTarget.style.opacity = '1';
        } else if (!event.empty) {
          this.cardNumDisplayTarget.textContent = '•••• •••• •••• ••••';
          this.cardNumDisplayTarget.style.opacity = '0.7';
        } else {
          this.cardNumDisplayTarget.textContent = '•••• •••• •••• ••••';
          this.cardNumDisplayTarget.style.opacity = '1';
        }
      }
    })

    this.cardExpiryElement.on('change', (event) => {
      this.handleError(event)
      if (this.hasCardExpDisplayTarget) {
        this.cardExpDisplayTarget.textContent = !event.empty ? '••/••' : 'MM/YY';
      }
    })

    this.cardCvcElement.on('change', this.handleError.bind(this))

    // CVV フォーカスで裏面フリップ
    this.cardCvcElement.on('focus', () => {
      if (this.hasCard3DTarget) {
        this.card3DTarget.classList.add('is-flipped');
        this.card3DTarget.style.transform = '';
      }
    })

    this.cardCvcElement.on('blur', () => {
      if (this.hasCard3DTarget) {
        this.card3DTarget.classList.remove('is-flipped');
      }
    })
  }

  handleError(event) {
    if (event.error) {
      this.cardErrorsTarget.textContent = event.error.message
    } else {
      this.cardErrorsTarget.textContent = ''
    }
  }

  updateBrand(brand) {
    if (!this.hasBrandLogoSlotTarget) return;
    
    if (brand === 'visa' && this.hasVisaLogoValue) {
      this.brandLogoSlotTarget.innerHTML = `<img src="${this.visaLogoValue}" style="height: 32px; width: auto; object-fit: contain;">`;
    } else if (brand === 'mastercard' && BRAND_SVGS.mastercard) {
      this.brandLogoSlotTarget.innerHTML = BRAND_SVGS.mastercard;
    } else if (brand === 'amex' && this.hasAmexLogoValue) {
      this.brandLogoSlotTarget.innerHTML = `<div style="width: 44px; height: 28px; overflow: hidden; border-radius: 4px; display: flex; align-items: center; justify-content: center; background: #fff;"><img src="${this.amexLogoValue}" style="width: 100%; height: 100%; object-fit: contain; transform: scale(1.4);"></div>`;
    } else if (brand === 'jcb' && this.hasJcbLogoValue) {
      this.brandLogoSlotTarget.innerHTML = `<div style="width: 44px; height: 28px; overflow: hidden; border-radius: 4px; display: flex; align-items: center; justify-content: center; background: #fff;"><img src="${this.jcbLogoValue}" style="width: 100%; height: 100%; object-fit: contain; transform: scale(1.7);"></div>`;
    } else if (this.hasVisaLogoValue) {
      this.brandLogoSlotTarget.innerHTML = `<img src="${this.visaLogoValue}" style="height: 32px; width: auto; object-fit: contain;">`;
    }
  }
  
  async submit(event) {
    event.preventDefault()
    
    if (this.submitButtonTarget.classList.contains('processing') || this.submitButtonTarget.classList.contains('success')) return;

    this.submitButtonTarget.classList.add('processing');
    this.submitButtonTarget.disabled = true;
    
    try {
      // Setup Intent作成
      const response = await fetch('/billing/setup_intent', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': this.getCSRFToken(),
          'Accept': 'application/json'
        }
      })
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      
      const { client_secret, error } = await response.json()
      
      if (error) {
        throw new Error(error)
      }
      
      // カード確認
      const { error: confirmError, setupIntent } = await this.stripe.confirmCardSetup(client_secret, {
        payment_method: {
          card: this.cardNumberElement,
          billing_details: {
            name: this.hasCardNameInputTarget ? this.cardNameInputTarget.value : undefined
          }
        }
      })
      
      if (confirmError) {
        throw new Error(confirmError.message)
      } else {
        // 成功時の処理
        const confirmResponse = await fetch('/billing/confirm_card', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': this.getCSRFToken()
          },
          body: JSON.stringify({
            setup_intent_id: setupIntent.id
          })
        })
        
        if (confirmResponse.ok) {
          this.submitButtonTarget.classList.remove('processing');
          this.submitButtonTarget.classList.add('success');
          
          setTimeout(() => {
            location.reload();
          }, 1500); // 1.5秒成功アニメーションを見せてからリロード
          return; // finally でボタンを戻さないようにする
        } else {
          const result = await confirmResponse.json()
          throw new Error(result.error || 'エラーが発生しました')
        }
      }
    } catch (error) {
      this.cardErrorsTarget.textContent = error.message || 'エラーが発生しました'
      this.submitButtonTarget.classList.remove('processing');
      this.submitButtonTarget.disabled = false;
    }
  }
  
  getCSRFToken() {
    const tokenElement = document.querySelector('[name="csrf-token"]')
    return tokenElement ? tokenElement.content : ''
  }
}